import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Button,
    TextField, InputAdornment, CircularProgress, Alert,
    List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
    Chip, Divider, Tooltip,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

// ─── helpers ──────────────────────────────────────────────────────────────────

const avatarLetter = (pid) => (pid ? pid.charAt(0).toUpperCase() : "?");

const avatarColor = (pid) => {
    if (!pid) return "#94a3b8";
    const hue = Math.abs(pid.split("").reduce((acc, c) => acc * 31 + c.charCodeAt(0), 0)) % 360;
    return `hsl(${hue}, 55%, 48%)`;
};

const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("fr-FR", { dateStyle: "short" }) : "–";

// ─── SearchCustomerPanel ──────────────────────────────────────────────────────

const SearchCustomerPanel = ({ brandName, existingIds, onAdd, segmentId }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState({});
    const debounceRef = useRef(null);

    const search = useCallback(
        async (q) => {
            if (!q.trim()) { setResults([]); return; }
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({ q: q.trim(), limit: "20" });
                const res = await loyaltyFetch(
                    `${LOYALTY_API_BASE}/customers?${params}`,
                    { headers: { "X-Brand": brandName } },
                );
                if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
                const data = await res.json();
                setResults(Array.isArray(data.items) ? data.items : []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        },
        [brandName],
    );

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => search(query), 380);
        return () => clearTimeout(debounceRef.current);
    }, [query, search]);

    const handleAdd = async (customer) => {
        setAdding((p) => ({ ...p, [customer.id]: true }));
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segmentId}/members`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ customer_id: customer.id }),
                },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            const member = await res.json();
            onAdd(member, customer);
        } catch (err) {
            setError(err.message);
        } finally {
            setAdding((p) => ({ ...p, [customer.id]: false }));
        }
    };

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par profil ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            {error && (
                <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>
                    {error}
                </Alert>
            )}

            {results.length > 0 && (
                <List
                    dense
                    disablePadding
                    sx={{
                        mt: 1,
                        maxHeight: 220,
                        overflowY: "auto",
                        border: "1px solid #e8ecf1",
                        borderRadius: 2,
                        bgcolor: "#fff",
                    }}
                >
                    {results.map((c, idx) => {
                        const alreadyIn = existingIds.has(c.id);
                        const isAdding = adding[c.id];
                        return (
                            <React.Fragment key={c.id}>
                                {idx > 0 && <Divider sx={{ mx: 1 }} />}
                                <ListItem sx={{ py: 0.75, px: 1.5 }}>
                                    <ListItemAvatar sx={{ minWidth: 36 }}>
                                        <Avatar
                                            sx={{
                                                width: 28, height: 28,
                                                fontSize: 12, fontWeight: 700,
                                                bgcolor: avatarColor(c.profile_id),
                                            }}
                                        >
                                            {avatarLetter(c.profile_id)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>
                                                {c.profile_id}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography fontSize={10} color="text.secondary">
                                                {c.loyalty_status_name || c.loyalty_status || "–"} · {c.status_points ?? 0} pts
                                            </Typography>
                                        }
                                        sx={{ my: 0 }}
                                    />
                                    <ListItemSecondaryAction>
                                        {alreadyIn ? (
                                            <Chip
                                                label="Déjà membre"
                                                size="small"
                                                icon={<CheckCircleIcon sx={{ fontSize: "12px !important" }} />}
                                                sx={{ fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a", height: 22 }}
                                            />
                                        ) : (
                                            <Tooltip title="Ajouter au segment">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isAdding}
                                                        onClick={() => handleAdd(c)}
                                                        sx={{
                                                            color: SECTION_COLORS.segments.main,
                                                            bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                            width: 26, height: 26,
                                                            "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                        }}
                                                    >
                                                        {isAdding
                                                            ? <CircularProgress size={14} color="inherit" />
                                                            : <PersonAddIcon sx={{ fontSize: 14 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}

            {!loading && query.trim() && results.length === 0 && (
                <Typography fontSize={12} color="text.secondary" mt={1} textAlign="center">
                    Aucun client trouvé pour « {query} »
                </Typography>
            )}
        </Box>
    );
};

// ─── MembersList ──────────────────────────────────────────────────────────────

const MembersList = ({ members, profiles, removing, onRemove }) => {
    if (members.length === 0) {
        return (
            <Box
                display="flex" flexDirection="column" alignItems="center"
                justifyContent="center" py={4} gap={1}
                sx={{ border: "1px dashed #e2e8f0", borderRadius: 2, bgcolor: "#fafbfd" }}
            >
                <PeopleAltIcon sx={{ fontSize: 36, color: "#cbd5e1" }} />
                <Typography fontSize={12} color="text.secondary">
                    Aucun membre dans ce segment
                </Typography>
            </Box>
        );
    }

    return (
        <List
            dense
            disablePadding
            sx={{
                maxHeight: 280,
                overflowY: "auto",
                border: "1px solid #e8ecf1",
                borderRadius: 2,
                bgcolor: "#fff",
            }}
        >
            {members.map((m, idx) => {
                const prof = profiles[m.customer_id] || null;
                const pid = prof?.profile_id || m.customer_id;
                const isRemoving = removing[m.customer_id];
                return (
                    <React.Fragment key={m.customer_id}>
                        {idx > 0 && <Divider sx={{ mx: 1 }} />}
                        <ListItem sx={{ py: 0.75, px: 1.5 }}>
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar
                                    sx={{
                                        width: 28, height: 28,
                                        fontSize: 12, fontWeight: 700,
                                        bgcolor: avatarColor(pid),
                                    }}
                                >
                                    {avatarLetter(pid)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Typography fontSize={12} fontWeight={600} color="#1a1a2e" noWrap>
                                        {pid}
                                    </Typography>
                                }
                                secondary={
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography fontSize={10} color="text.secondary">
                                            Ajouté le {fmtDate(m.created_at)}
                                        </Typography>
                                        <Chip
                                            label={m.source}
                                            size="small"
                                            sx={{
                                                fontSize: 9, height: 16,
                                                bgcolor: m.source === "STATIC" ? "#eff6ff" : "#f0fdf4",
                                                color: m.source === "STATIC" ? "#1d4ed8" : "#15803d",
                                            }}
                                        />
                                    </Box>
                                }
                                sx={{ my: 0 }}
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title="Retirer du segment">
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={isRemoving}
                                            onClick={() => onRemove(m.customer_id)}
                                            sx={{
                                                color: "#DC2626",
                                                bgcolor: "#fef2f2",
                                                width: 26, height: 26,
                                                "&:hover": { bgcolor: "#fee2e2" },
                                            }}
                                        >
                                            {isRemoving
                                                ? <CircularProgress size={14} color="inherit" />
                                                : <PersonRemoveIcon sx={{ fontSize: 14 }} />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    </React.Fragment>
                );
            })}
        </List>
    );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────

const ManageSegmentMembersDialog = ({ open, segment, brandName, onClose }) => {
    const [members, setMembers] = useState([]);
    const [profiles, setProfiles] = useState({}); // customer_id → CustomerOut
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [membersError, setMembersError] = useState(null);
    const [removing, setRemoving] = useState({});

    // Set of customer IDs already in segment (for duplicate check in search)
    const existingIds = new Set(members.map((m) => m.customer_id));

    // ── Load members ────────────────────────────────────────────────────────
    const loadMembers = useCallback(async () => {
        if (!segment || !brandName) return;
        setLoadingMembers(true);
        setMembersError(null);
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members`,
                { headers: { "X-Brand": brandName } },
            );
            if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
            const data = await res.json();
            setMembers(Array.isArray(data) ? data : []);
        } catch (err) {
            setMembersError(err.message);
        } finally {
            setLoadingMembers(false);
        }
    }, [segment, brandName]);

    useEffect(() => {
        if (open) loadMembers();
        else { setMembers([]); setProfiles({}); }
    }, [open, loadMembers]);

    // ── Remove member ───────────────────────────────────────────────────────
    const handleRemove = async (customerId) => {
        setRemoving((p) => ({ ...p, [customerId]: true }));
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/segments/${segment.id}/members/${customerId}`,
                { method: "DELETE", headers: { "X-Brand": brandName } },
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            setMembers((prev) => prev.filter((m) => m.customer_id !== customerId));
        } catch {
            /* swallow — keep UI stable */
        } finally {
            setRemoving((p) => ({ ...p, [customerId]: false }));
        }
    };

    // ── Add member (callback from SearchCustomerPanel) ──────────────────────
    const handleAdd = (member, customerOut) => {
        setMembers((prev) => {
            if (prev.some((m) => m.customer_id === member.customer_id)) return prev;
            return [member, ...prev];
        });
        if (customerOut) {
            setProfiles((p) => ({ ...p, [member.customer_id]: customerOut }));
        }
    };

    if (!segment) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    borderBottom: "2px solid #f1f3f5",
                    px: 3, py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography
                            variant="h6"
                            fontFamily="Montserrat, sans-serif"
                            fontWeight={700}
                            color="#1a1a2e"
                            fontSize={15}
                        >
                            Membres — {segment.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Segment statique · gestion manuelle
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "20px !important", px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>

                {/* ── Current member count badge ── */}
                <Box
                    display="flex" alignItems="center" gap={1}
                    px={2} py={1}
                    sx={{
                        borderRadius: 2,
                        bgcolor: `${SECTION_COLORS.segments.main}08`,
                        border: `1px solid ${SECTION_COLORS.segments.main}20`,
                    }}
                >
                    <PeopleAltIcon sx={{ fontSize: 18, color: SECTION_COLORS.segments.main }} />
                    <Typography fontSize={13} fontWeight={600} color={SECTION_COLORS.segments.main}>
                        {members.length} membre{members.length !== 1 ? "s" : ""}
                    </Typography>
                    {loadingMembers && <CircularProgress size={14} sx={{ ml: "auto" }} />}
                </Box>

                {membersError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{membersError}</Alert>
                )}

                {/* ── Add member section ── */}
                <Box>
                    <Typography fontSize={12} fontWeight={700} color="#334155" mb={1} display="flex" alignItems="center" gap={0.5}>
                        <PersonAddIcon sx={{ fontSize: 14 }} />
                        Ajouter un client
                    </Typography>
                    <SearchCustomerPanel
                        brandName={brandName}
                        existingIds={existingIds}
                        segmentId={segment.id}
                        onAdd={handleAdd}
                    />
                </Box>

                <Divider sx={{ borderColor: "#f1f3f5" }} />

                {/* ── Members list ── */}
                <Box>
                    <Typography fontSize={12} fontWeight={700} color="#334155" mb={1} display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                        Membres actuels
                    </Typography>

                    {loadingMembers ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <MembersList
                            members={members}
                            profiles={profiles}
                            removing={removing}
                            onRemove={handleRemove}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageSegmentMembersDialog;
